import {PopupDropdown} from '@wandb/weave/common/components/PopupDropdown';
import {Button} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {ApolloQueryResult} from 'apollo-client';
import React, {useCallback, useState} from 'react';

import {EntityIntegrationsQuery, EntityQuery} from '../../generated/graphql';
import {useTeamMember} from '../../state/graphql/teamMembers';
import {useViewer} from '../../state/viewer/hooks';
import {DeleteGenericWebhookIntegrationModal} from './DeleteGenericWebhookIntegrationModal';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {TestWebhookDrawer} from './TestWebhookDrawer';
import {ViewWebhookDrawer} from './ViewWebhookDrawer';
import {WebhookDrawer} from './WebhookDrawer';
import {GenericWebhook} from './WebhooksTable';

enum WebhookActionModals {
  DeleteWebhook = 'DELETE_WEBHOOK',
  ViewWebhookDetails = 'VIEW_WEBHOOK_DETAILS',
  EditWebhook = 'EDIT_WEBHOOK',
  TestWebhook = 'TEST_WEBHOOK',
}

type WebhookActionsProps = {
  entityName: string;
  webhook: GenericWebhook;
  existingWebhooks?: GenericWebhook[];
  isPremiumFeatureEnabled?: boolean;
  isPersonalEntity?: boolean;
  entityRefetch?: () =>
    | Promise<ApolloQueryResult<EntityQuery>>
    | Promise<ApolloQueryResult<EntityIntegrationsQuery>>;
};

export const WebhookActions = ({
  entityName,
  webhook,
  existingWebhooks,
  isPremiumFeatureEnabled,
  isPersonalEntity,
  entityRefetch,
}: WebhookActionsProps) => {
  const viewer = useViewer();
  const {viewerIsTeamAdmin} = useTeamMember({
    viewer,
    entityName,
  });
  const hasSecrets =
    webhook.secretRef !== null || webhook.accessTokenRef !== null;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [webhookModal, setWebhookModal] = useState<WebhookActionModals>();
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setWebhookModal(undefined);
  }, [setIsModalOpen, setWebhookModal]);

  const onClickEditWebhook = useCallback(() => {
    setIsModalOpen(true);
    setWebhookModal(WebhookActionModals.EditWebhook);
  }, [setIsModalOpen, setWebhookModal]);
  const menuOptions = [
    [
      {
        key: 'view-webhook-details',
        text: 'View details',
        icon: <Icon name="list" style={{marginRight: '4px'}} />,
        onClick: () => {
          setIsModalOpen(true);
          setWebhookModal(WebhookActionModals.ViewWebhookDetails);
        },
      },
      {
        key: 'edit-webhook',
        text: 'Edit',
        disabled: !isPremiumFeatureEnabled,
        icon: <Icon name="pencil-edit" style={{marginRight: '4px'}} />,
        onClick: onClickEditWebhook,
      },
      {
        key: 'test-webhook',
        text: 'Test',
        icon: <Icon name="running-repeat" style={{marginRight: '4px'}} />,
        onClick: () => {
          setIsModalOpen(true);
          setWebhookModal(WebhookActionModals.TestWebhook);
        },
      },
    ],
    [
      {
        key: 'delete-webhook',
        text: 'Delete',
        icon: <Icon name="delete" style={{marginRight: '4px'}} />,
        onClick: () => {
          setIsModalOpen(true);
          setWebhookModal(WebhookActionModals.DeleteWebhook);
        },
        disabled: !viewerIsTeamAdmin && hasSecrets,
      },
    ],
  ];
  return (
    <Tailwind>
      <div className="flex h-full justify-end">
        <Button
          className="mr-8"
          icon="pencil-edit"
          variant="ghost"
          disabled={!isPremiumFeatureEnabled}
          onClick={onClickEditWebhook}
        />
        <PopupDropdown
          position="bottom right"
          trigger={<Button icon="overflow-horizontal" variant="ghost" />}
          sections={menuOptions}
          offset={`-8px, -8px`}
        />
        <DeleteGenericWebhookIntegrationModal
          isOpen={
            isModalOpen && webhookModal === WebhookActionModals.DeleteWebhook
          }
          closeModal={closeModal}
          webhookIntegrationID={webhook.id}
          webhookIntegrationName={webhook.name}
          entityRefetch={entityRefetch}
        />
        <ViewWebhookDrawer
          open={
            isModalOpen &&
            webhookModal === WebhookActionModals.ViewWebhookDetails
          }
          closeDrawer={closeModal}
          genericWebhook={webhook}
          entityName={entityName}
          isPersonalEntity={isPersonalEntity}
        />
        <WebhookDrawer
          open={isModalOpen && webhookModal === WebhookActionModals.EditWebhook}
          closeDrawer={closeModal}
          drawerMode="Edit"
          entityName={entityName}
          refetch={entityRefetch}
          existingWebhooks={existingWebhooks}
          isPersonalEntity={isPersonalEntity}
          webhook={webhook}
        />
        <TestWebhookDrawer
          open={isModalOpen && webhookModal === WebhookActionModals.TestWebhook}
          closeDrawer={closeModal}
          entityName={entityName}
          webhook={webhook}
        />
      </div>
    </Tailwind>
  );
};
